import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Ticket, Plus, Edit, Trash2, Copy } from 'lucide-react'
import { toast } from 'sonner'

interface Coupon {
  id: number
  code: string
  description: string | null
  discount_type: string
  discount_value: number
  formatted_discount: string
  min_purchase: number
  max_uses: number | null
  uses_count: number
  max_uses_per_user: number
  active: boolean
  expired: boolean
  status_label: string
  expires_at: string | null
  created_at: string
}

interface Props {
  coupons: Coupon[]
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  Ativo: 'default',
  Inativo: 'secondary',
  Expirado: 'destructive',
  Esgotado: 'outline'
}

export default function CouponsIndex({ coupons }: Props) {
  const handleDelete = (id: number, code: string) => {
    if (confirm(`Tem certeza que deseja excluir o cupom "${code}"?`)) {
      router.delete(`/coupons/${id}`)
    }
  }

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    toast.success(`Código "${code}" copiado!`)
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Ticket className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Cupons de Desconto</h1>
              <p className="text-sm text-muted-foreground">
                {coupons.length} {coupons.length === 1 ? 'cupom' : 'cupons'}
              </p>
            </div>
          </div>
          <Button onClick={() => router.visit('/coupons/new')}>
            <Plus className="h-4 w-4 mr-1" />
            Novo Cupom
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Desconto</TableHead>
                  <TableHead className="text-center">Usos</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Ticket className="h-10 w-10" />
                        <p>Nenhum cupom criado</p>
                        <Button size="sm" onClick={() => router.visit('/coupons/new')}>
                          <Plus className="h-4 w-4 mr-1" />
                          Criar Primeiro Cupom
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  coupons.map((coupon) => (
                    <TableRow key={coupon.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-1 bg-muted rounded text-sm font-mono font-bold">
                            {coupon.code}
                          </code>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyCode(coupon.code)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{coupon.description || '—'}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono">
                          {coupon.formatted_discount}
                        </Badge>
                        {coupon.min_purchase > 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Min: R$ {coupon.min_purchase.toFixed(2)}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">
                          {coupon.uses_count}{coupon.max_uses ? ` / ${coupon.max_uses}` : ''}
                        </span>
                      </TableCell>
                      <TableCell>
                        {coupon.expires_at ? (
                          <span className="text-sm text-muted-foreground">
                            {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sem validade</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={STATUS_VARIANT[coupon.status_label] || 'outline'}>
                          {coupon.status_label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => router.visit(`/coupons/${coupon.id}/edit`)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(coupon.id, coupon.code)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
